import { useAlert } from 'components/alert/AlertStack';
import { classNames } from 'libs/react';
import { createContext } from 'libs/react';
import { useContext } from 'libs/react';
import { useEffect } from 'libs/react';
import { useRef } from 'libs/react';
import { useValue } from 'libs/react';
import { useDebounce } from 'libs/react/hook/useDebounce';
import { append } from 'libs/util/array';
import { remove } from 'libs/util/array';
import { flat } from 'libs/util/object';
import { nest } from 'libs/util/object';
import { React } from 'libs/react';
import './Form.scss';
/**
 * @component Form
 * @since 1.0.0
 */
export function Form(props) {
    const alert = useAlert();
    let { id, name, style, error, processing, successful, manage, retain, preventDefault } = Object.assign({ style: 'normal', manage: true, retain: true, processing: false, successful: false, preventDefault: true }, props);
    const node = useRef(null);
    const onChange = useDebounce(props.onChange);
    const onSubmit = useDebounce(props.onSubmit);
    const invokeChangeListeners = useDebounce(() => self.changeListeners.forEach((listener) => listener.call(null)));
    const invokeSubmitListeners = useDebounce(() => self.submitListeners.forEach((listener) => listener.call(null)));
    const invokeResetListeners = useDebounce(() => self.resetListeners.forEach((listener) => listener.call(null)));
    const self = useValue(() => ({
        values: {},
        changeListeners: [],
        submitListeners: [],
        resetListeners: []
    }));
    /**
     * @function getValues
     * @since 1.0.0
     * @hidden
     */
    function getValues() {
        return nest(self.values);
    }
    /**
     * @function setValues
     * @since 1.0.0
     * @hidden
     */
    function setValues(values) {
        self.values = flat(values);
    }
    /**
     * @function submit
     * @since 1.0.0
     * @hidden
     */
    function submit() {
        invokeSubmitListeners();
        if (onSubmit) {
            onSubmit(getValues());
        }
    }
    /**
     * @function updateValue
     * @since 1.0.0
     * @hidden
     */
    function updateValue(field, value) {
        if (manage == false) {
            return;
        }
        if (field == '' || field == null) {
            return;
        }
        if (self.values[field] == value) {
            return;
        }
        self.values[field] = value;
        if (onChange) {
            onChange(getValues());
        }
        invokeChangeListeners();
    }
    /**
     * @function removeValue
     * @since 1.0.0
     * @hidden
     */
    function removeValue(field, force = false) {
        if ((retain || manage == false) && force == false) {
            return;
        }
        if (field == '' || field == null) {
            return;
        }
        delete self.values[field];
        if (onChange) {
            onChange(getValues());
        }
        invokeChangeListeners();
    }
    /**
     * @function hasValue
     * @since 1.0.0
     * @hidden
     */
    function hasValue(field) {
        return field in self.values;
    }
    /**
     * @function getValue
     * @since 1.0.0
     * @hidden
     */
    function getValue(field) {
        return self.values[field];
    }
    /**
     * @function getError
     * @since 1.0.0
     * @hidden
     */
    function getError(field) {
        if (error == null) {
            return null;
        }
        for (const graphQLError of error.graphQLErrors) {
            if (graphQLError.extensions && graphQLError.extensions.fields) {
                return graphQLError.extensions.fields[field];
            }
        }
        return null;
    }
    /**
     * @function appendChangeListener
     * @since 1.0.0
     * @hidden
     */
    function appendChangeListener(listener) {
        append(self.changeListeners, listener);
    }
    /**
     * @function removeChangeListener
     * @since 1.0.0
     * @hidden
     */
    function removeChangeListener(listener) {
        remove(self.changeListeners, listener);
    }
    /**
     * @function appendSubmitListener
     * @since 1.0.0
     * @hidden
     */
    function appendSubmitListener(listener) {
        append(self.submitListeners, listener);
    }
    /**
     * @function removeSubmitListener
     * @since 1.0.0
     * @hidden
     */
    function removeSubmitListener(listener) {
        remove(self.submitListeners, listener);
    }
    /**
     * @function appendResetListener
     * @since 1.0.0
     * @hidden
     */
    function appendResetListener(listener) {
        append(self.resetListeners, listener);
    }
    /**
     * @function removeResetListener
     * @since 1.0.0
     * @hidden
     */
    function removeResetListener(listener) {
        remove(self.resetListeners, listener);
    }
    /**
     * @function reset
     * @since 1.0.0
     * @hidden
     */
    function reset() {
        invokeResetListeners();
    }
    /**
     * @function onFormSubmit
     * @since 1.0.0
     * @hidden
     */
    function onFormSubmit(event) {
        // Check HTML5 validation before submitting
        if (!event.currentTarget.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
            // Trigger validation UI
            event.currentTarget.reportValidity();
            return;
        }
        if (preventDefault) {
            event.preventDefault();
        }
        submit();
    }
    useEffect(() => {
        if (error == null) {
            return;
        }
        let field = '';
        for (const graphQLError of error.graphQLErrors) {
            const fields = graphQLError.extensions && graphQLError.extensions.fields;
            if (fields) {
                for (const key in fields) {
                    const exist = hasValue(key);
                    if (exist == false) {
                        field = key;
                        break;
                    }
                }
            }
        }
        alert.present({
            error,
            field
        });
    }, [error]);
    useEffect(() => {
        if (node.current) {
            node.current.querySelectorAll('[type=submit]').forEach((button) => {
                button.classList.toggle('button--is-processing', processing);
                button.classList.toggle('button--is-successful', successful);
            });
        }
    }, [processing, successful]);
    if (name) {
        name = name + '-form';
    }
    const context = {
        submit,
        removeValue,
        updateValue,
        getValue,
        hasValue,
        getError,
        getValues,
        setValues,
        reset,
        appendChangeListener,
        removeChangeListener,
        appendSubmitListener,
        removeSubmitListener,
        appendResetListener,
        removeResetListener
    };
    const className = classNames('form', 'form--' + style, { 'form--has-error': error }, { 'form--is-processing': processing }, { 'form--is-successful': successful }, props.className, name);
    return (React.createElement(FormContext.Provider, { value: context },
        React.createElement("form", { id: id, ref: node, name: name, className: className, onSubmit: onFormSubmit }, props.children)));
}
/**
 * @component FormContext
 * @since 1.0.0
 */
export const FormContext = createContext({
    submit() { },
    removeValue() { },
    updateValue() { },
    hasValue() {
        return false;
    },
    getValue() {
        return null;
    },
    getError() {
        return null;
    },
    getValues() {
        return {};
    },
    setValues() { },
    appendChangeListener() { },
    removeChangeListener() { },
    appendSubmitListener() { },
    removeSubmitListener() { },
    appendResetListener() { },
    removeResetListener() { },
    reset() { }
});
/**
 * @component useForm
 * @since 1.0.0
 */
export function useForm() {
    return useContext(FormContext);
}
